import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Section, StatusPill, EmptyState } from "@/components/app/Primitives";
import { InviteInfluencerDialog, IssuePODialog } from "@/components/app/OrderIntakeActions";
import QuotationUploadPanel from "@/components/app/QuotationUploadPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import TID from "@/lib/testIds";

// SEC: stable client-side row id for React reconciliation. Stripped before
// the payload is sent to the backend so it never lands in the API contract.
const _rowKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `r_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const _initialServices = () => [
  { _key: _rowKey(), type: "Instagram Reel", count: 1, description: "", fee: 0 },
];

const emptyForm = {
  influencer_name: "", influencer_email: "", influencer_mobile: "", influencer_country: "India",
  campaign: "", product: "",
  services: _initialServices(),
  order_value: 0, currency: "INR",
  fmcg_po_number: "", fmcg_po_date: new Date().toISOString().slice(0, 10),
  fmcg_po_file_url: null, fmcg_po_filename: null,
  influencer_payment_terms_days: 30, customer_terms_days: 60,
  expected_delivery_date: "",
  primary_contact_email: "", primary_contact_name: "",
  advance_required: false, advance_mode: "percentage", advance_percentage: 50, advance_amount: 0,
  advance_trigger: "on_po_acceptance", advance_reason: "",
  notes: "",
};

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [inviteFor, setInviteFor] = useState(null);
  const [issuePoFor, setIssuePoFor] = useState(null);
  // Live tenant fee config (propay_fee_pct + early_payment_fee_rate_per_30)
  // so the customer sees the transparent cost breakdown as they type.
  const [tenantFees, setTenantFees] = useState({
    management_fee_pct: 2.18,
    payment_terms_diff_rate_per_30: 1.5,
    // legacy fields kept for existing Procurement-side dialog
    propay_fee_pct: 10,
    early_payment_fee_rate_per_30: 1.5,
  });
  // Two-phase state:
  //  phase = 'draft'      → filling the form, primary CTA = "Generate Quotation"
  //  phase = 'quoted'     → quotation generated, primary CTA = "Upload FMCG PO & Submit"
  const [phase, setPhase] = useState("draft");
  const [quotation, setQuotation] = useState(null); // { quotation_number, quotation_url, lines, order }
  const isCustomer = user.role === "customer";

  const load = useCallback(() => api.get("/orders").then((r) => setOrders(r.data)), []);
  useEffect(() => {
    load();
    if (isCustomer) {
      setF((v) => ({ ...v, primary_contact_email: user.email, primary_contact_name: user.name }));
      // Fetch the fee configuration set by ProPay Admin so we can show the
      // customer exactly what ProPay charges on top of the influencer cost.
      api.get(`/tenants/${user.tenant_id}`).then((r) => {
        setTenantFees({
          management_fee_pct: Number(r.data.management_fee_pct ?? 2.18),
          payment_terms_diff_rate_per_30: Number(r.data.payment_terms_diff_rate_per_30 ?? 1.5),
          propay_fee_pct: Number(r.data.propay_fee_pct ?? 10),
          early_payment_fee_rate_per_30: Number(r.data.early_payment_fee_rate_per_30 ?? 1.5),
        });
      }).catch(() => {});
    }
  }, [load, isCustomer, user.email, user.name, user.tenant_id]);

  const updateService = (i, key, val) => setF({
    ...f, services: f.services.map((s, idx) => idx === i ? { ...s, [key]: val } : s)
  });
  const addService = () => setF({ ...f, services: [...f.services, { _key: _rowKey(), type: "Instagram Post", count: 1, description: "", fee: 0 }] });
  const removeService = (i) => setF({ ...f, services: f.services.filter((_, idx) => idx !== i) });
  const totalFromServices = f.services.reduce((a, s) => a + (parseFloat(s.fee) || 0), 0);

  // ============================================================
  // 3-LINE QUOTATION BREAKDOWN (matches backend _compute_quotation_lines):
  //   Line 1 · COGS               = sum of service-line fees (influencer's actual)
  //   Line 2 · ProPay Mgmt Fee    = COGS × management_fee_pct           (flat)
  //   Line 3 · Payment Mgmt Fee   = term-diff cost + advance cost
  //       term_diff = COGS × rate × (customer_terms − influencer_terms)/30
  //       advance   = COGS × advance% × rate × customer_terms/30
  // Total = 1 + 2 + 3. This IS the FMCG PO value payable to ProPay.
  // ============================================================
  const cogs = totalFromServices;
  const mgmtPct = Number(tenantFees.management_fee_pct || 2.18);
  const mgmtFee = Math.round(cogs * (mgmtPct / 100) * 100) / 100;
  const rate30 = Number(tenantFees.payment_terms_diff_rate_per_30 || 1.5);
  const custTerms = parseInt(f.customer_terms_days, 10) || 60;
  const infTerms = parseInt(f.influencer_payment_terms_days, 10) || 30;
  const daysDiff = Math.max(custTerms - infTerms, 0);
  const termDiffCost = Math.round(cogs * (rate30 / 100) * (daysDiff / 30) * 100) / 100;
  const advancePct = f.advance_required ? (parseFloat(f.advance_percentage) || 0) : 0;
  const advanceCost = advancePct
    ? Math.round(cogs * (advancePct / 100) * (rate30 / 100) * (custTerms / 30) * 100) / 100
    : 0;
  const paymentMgmtFee = Math.round((termDiffCost + advanceCost) * 100) / 100;
  const fmcgPoTotal = Math.round((cogs + mgmtFee + paymentMgmtFee) * 100) / 100;
  const fmt = (n) => `${f.currency} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  // Kept for legacy Procurement dialog (Issue-PO fee-split banner still uses these)
  const influencerActualCost = cogs;
  const propayFee = mgmtFee;
  const advanceAmount = f.advance_required
    ? (f.advance_mode === "percentage"
        ? Math.round(cogs * ((parseFloat(f.advance_percentage) || 0) / 100) * 100) / 100
        : (parseFloat(f.advance_amount) || 0))
    : 0;
  const earlyPaymentFee = paymentMgmtFee;

  const generateQuotation = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Phase 1 — create the order in `quotation_pending` state and generate
      // the ProPay quotation PDF. Customer downloads the PDF and uses it to
      // raise their internal FMCG PO before phase 2.
      const body = {
        ...f,
        services: f.services.map(({ _key, ...rest }) => rest),
        order_value: fmcgPoTotal || totalFromServices,
        advance_percentage: f.advance_mode === "percentage" ? parseFloat(f.advance_percentage) || 0 : 0,
        advance_amount: f.advance_mode === "amount" ? parseFloat(f.advance_amount) || 0 : null,
      };
      delete body.advance_mode;
      // FMCG PO details are NOT required in phase 1 — the customer will
      // provide these in phase 2 after generating their internal PO.
      body.fmcg_po_number = ""; body.fmcg_po_date = ""; body.fmcg_po_file_url = null; body.fmcg_po_filename = null;
      const createResp = await api.post("/orders", body);
      const oid = createResp.data.id;
      const qResp = await api.post(`/orders/${oid}/quotation`, {});
      setQuotation({ ...qResp.data, order_id: oid });
      setPhase("quoted");
      toast.success(`Quotation ${qResp.data.quotation_number} generated`, {
        description: "You can close and resume anytime — the order will show 'Upload PO' in your Orders list.",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate quotation");
    } finally {
      setBusy(false);
    }
  };

  const submitPOToProcurement = async (fmcgPoNumber, fmcgPoDate, fmcgPoFileUrl, fmcgPoFilename) => {
    if (!quotation?.order_id) return;
    setBusy(true);
    try {
      await api.post(`/orders/${quotation.order_id}/submit-to-procurement`, {
        fmcg_po_number: fmcgPoNumber,
        fmcg_po_date: fmcgPoDate,
        fmcg_po_file_url: fmcgPoFileUrl,
        fmcg_po_filename: fmcgPoFilename,
      });
      toast.success("FMCG PO submitted to ProPay Procurement Desk");
      setOpen(false);
      setPhase("draft"); setQuotation(null);
      setF({ ...emptyForm, services: _initialServices() });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadQuotationPDF = async () => {
    if (!quotation?.order_id) return;
    try {
      const r = await api.get(`/orders/${quotation.order_id}/quotation.pdf`,
                               { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${quotation.quotation_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const invite = (o) => setInviteFor(o);
  const issuePO = (o) => setIssuePoFor(o);

  // Resume: reopen the dialog directly in phase-2 for an existing
  // quotation_generated order, so the customer can come back later and upload
  // their FMCG PO without losing state. For quotation_pending orders, the
  // quotation is auto-generated first so the resume always lands in phase-2.
  const resumeUploadPO = async (orderId) => {
    try {
      let o = (await api.get(`/orders/${orderId}`)).data;
      if (!o.quotation_number) {
        // Auto-generate the missing quotation so demo can proceed without
        // going back to fill the form.
        const qResp = await api.post(`/orders/${orderId}/quotation`, {});
        setQuotation({ ...qResp.data, order_id: orderId });
      } else {
        setQuotation({
          order_id: orderId,
          quotation_number: o.quotation_number,
          quotation_url: o.quotation_url,
          lines: o.quotation_lines || {
            cogs: 0, mgmt_fee: 0, payment_mgmt_fee: 0,
            total: o.order_value || 0, currency: o.currency || "INR",
          },
        });
      }
      setPhase("quoted");
      setOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load order");
    }
  };

  // Demo speed-up: one-click pre-fill of the entire order form with realistic
  // Unilever × Priya Sharma sample data.
  const fillSampleData = () => {
    setF({
      ...emptyForm,
      influencer_name: "Priya Sharma",
      influencer_email: "priya.influencer@example.com",
      influencer_mobile: "+91-9876543210",
      influencer_country: "India",
      campaign: "Diwali 2026 · Bru Coffee Festive Push",
      product: "Bru Instant Coffee",
      services: [
        { _key: _rowKey(), type: "Instagram Reel", count: 2, description: "Diwali morning reel + festive coffee moment", fee: 55000 },
        { _key: _rowKey(), type: "Instagram Post", count: 3, description: "Product hero shots — living-room + kitchen", fee: 25000 },
      ],
      influencer_payment_terms_days: 15,
      customer_terms_days: 60,
      currency: "INR",
      advance_required: true,
      advance_percentage: 30,
      advance_trigger: "on_po_acceptance",
      advance_reason: "Mobilisation deposit for creator ops",
      expected_delivery_date: new Date(Date.now() + 21 * 86400000).toISOString().slice(0, 10),
      primary_contact_email: user.email,
      primary_contact_name: user.name,
      notes: "DEMO — Diwali festive collaboration for Bru",
    });
    toast.success("Sample data filled — review and generate quotation");
  };

  return (
    <div className="space-y-6">
      <Section
        title={isCustomer ? "Orders in your campaigns" : "Order intake queue"}
        subtitle={isCustomer ? "Read-only view — orders now originate from AdFluence via ProPay Procurement. Use 'Request status update' to nudge Procurement." : "Review orders from FMCG customers and drive the influencer onboarding."}
        right={isCustomer ? null : null}
      />

      {orders.length === 0 ? (
        <EmptyState title="No orders yet" hint={isCustomer ? "Submit your first order to bring an influencer into the ProPay one-vendor flow." : "Orders submitted by FMCG customers will appear here."} />
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
              <tr>
                <th className="px-4 py-3 text-left">Order #</th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Campaign</th>
                <th className="px-4 py-3 text-left">Influencer</th>
                <th className="px-4 py-3 text-right">Value</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={TID.orderRow(o.id)} className="border-t hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs">{o.order_number}</td>
                  <td className="px-4 py-3">{o.tenant_name}</td>
                  <td className="px-4 py-3 font-semibold">
                    <Link to={o.id} className="hover:underline">{o.campaign}</Link>
                    <div className="text-xs text-slate-500">{o.product}</div>
                  </td>
                  <td className="px-4 py-3">{o.influencer_name}<div className="text-xs text-slate-500">{o.influencer_email}</div></td>
                  <td className="px-4 py-3 text-right font-semibold">{o.currency} {Number(o.order_value).toLocaleString()}</td>
                  <td className="px-4 py-3"><StatusPill status={o.status} /></td>
                  <td className="px-4 py-3 text-right">
                    {isCustomer && (o.status === "quotation_generated" || o.status === "quotation_pending") && (
                      <Button
                        data-testid={`resume-upload-po-${o.id}`}
                        size="sm"
                        className="bg-[#0052FF] hover:bg-[#0043D9]"
                        onClick={() => resumeUploadPO(o.id)}
                      >
                        Upload PO
                      </Button>
                    )}
                    {["procurement_desk", "propay_admin"].includes(user.role) && o.status === "submitted" && (
                      <Button data-testid={TID.inviteInfluencerBtn(o.id)} size="sm" variant="outline" onClick={() => invite(o)}>Verify & invite</Button>
                    )}
                    {["procurement_desk", "propay_admin"].includes(user.role) && o.status === "onboarding" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => invite(o)}>Re-send invite</Button>
                        <Button data-testid={TID.issuePoBtn(o.id)} size="sm" className="ml-2 bg-[#0052FF] hover:bg-[#0043D9]" onClick={() => issuePO(o)}>Review & issue PO</Button>
                      </>
                    )}
                    {["procurement_desk", "propay_admin"].includes(user.role) && o.status === "po_issued" && (
                      <span className="text-xs text-emerald-700 font-semibold">PO issued — awaiting acceptance</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {inviteFor && (
        <InviteInfluencerDialog
          order={inviteFor}
          open={!!inviteFor}
          onOpenChange={(o) => { if (!o) setInviteFor(null); }}
          onDone={load}
          testIdPrefix={`order-${inviteFor.id}`}
        />
      )}
      {issuePoFor && (
        <IssuePODialog
          order={issuePoFor}
          open={!!issuePoFor}
          onOpenChange={(o) => { if (!o) setIssuePoFor(null); }}
          onDone={load}
          testIdPrefix={`order-${issuePoFor.id}`}
        />
      )}
    </div>
  );
}
